import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Field, AccessibleButton, useAccessibility } from '../lib/accessibility'

// Public, pre-auth page where a user lands from a password-reset or invite
// email link to set a new password. Both flows hit the same backend endpoint
// (POST /auth/reset-confirm) — confirming the token sets the bcrypt password
// and, for invited owners, flips their account from 'invited' to 'active'.
//
//   /reset-password?token=...  → mode="reset"  (forgot-password flow)
//   /accept-invite?token=...   → mode="invite" (new tenant owner / member)
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

const C = {
  amber: '#F5A623',
  green: '#2D6A2F',
} as const

// Mango mark — same teardrop shape used on LoginPage / app sidebar
function MangoMark({ size = 64 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 44" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M20 38 C10 38 4 30 4 22 C4 13 10 8 17 7 C19 6 21 6 23 7 C30 9 36 15 36 24 C36 32 29 38 20 38Z"
        fill={C.amber}
      />
      <path
        d="M17 7 C10 8 4 14 4 22 C4 28 7 33 12 36 C8 32 7 26 9 20 C11 13 14 9 17 7Z"
        fill="#E07C12"
        opacity="0.45"
      />
      <ellipse cx="26" cy="15" rx="4" ry="6" fill="rgba(255,255,255,0.22)" transform="rotate(20 26 15)" />
      <path d="M19 7 C18 4 17 1 20 0" stroke={C.green} strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M20 0 C26 -1 28 5 23 6 C21 6 20 3 20 0Z" fill={C.green} />
      <path d="M20 0 C22 2 23 4 23 6" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" strokeLinecap="round" fill="none" />
    </svg>
  )
}

export default function SetPasswordPage({ mode = 'reset' }: { mode?: 'reset' | 'invite' }) {
  const { announce } = useAccessibility()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const isInvite = mode === 'invite'
  const heading = isInvite ? 'Set your password' : 'Choose a new password'
  const cta = isInvite ? 'Activate account' : 'Reset password'

  // If the link is missing its token, the user can't proceed — surface it up front.
  useEffect(() => {
    if (!token) {
      setErrors({ form: 'This link is missing its security token. Please request a new email.' })
      announce('This link is invalid. Please request a new email.', 'assertive')
    }
  }, [token, announce])

  function validate() {
    const e: Record<string, string> = {}
    if (!password) e.password = 'Password is required'
    else if (password.length < 8) e.password = 'Password must be at least 8 characters'
    if (confirm !== password) e.confirm = 'Passwords do not match'
    return e
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return
    const errs = validate()
    if (Object.keys(errs).length) {
      setErrors(errs)
      announce(`Form has ${Object.keys(errs).length} error${Object.keys(errs).length > 1 ? 's' : ''}. Please correct and try again.`, 'assertive')
      return
    }
    setErrors({})
    setLoading(true)
    try {
      const res = await fetch(`${BASE}/api/v1/auth/reset-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const msg = data.error || 'This link has expired or is invalid. Please request a new email.'
        setErrors({ form: msg })
        announce(msg, 'assertive')
        return
      }
      setDone(true)
      announce('Password set. Redirecting you to sign in.', 'polite')
      setTimeout(() => navigate('/login', { replace: true }), 1800)
    } catch {
      setErrors({ form: 'Network error. Please check your connection and try again.' })
      announce('Network error.', 'assertive')
    } finally {
      setLoading(false)
    }
  }

  const sw = { border: 'none', padding: 0, margin: 0 } as const

  return (
    <main id="main-content" style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-background-tertiary)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <header style={{ textAlign: 'center', marginBottom: 36 }}>
          <a
            href="https://grafty.io"
            aria-label="Grafty — go to homepage"
            style={{ textDecoration: 'none', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
          >
            <MangoMark size={72} />
            <span style={{
              fontSize: 32, fontWeight: 700, color: 'var(--color-text-primary)',
              fontFamily: '"Syne", system-ui, sans-serif', letterSpacing: '-0.02em', lineHeight: 1,
            }}>
              Grafty
            </span>
          </a>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
            {isInvite ? 'Welcome aboard — set a password to get started.' : 'Every tree, every harvest, every season.'}
          </p>
        </header>

        {/* Card */}
        <section style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 14, padding: 28 }}>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: '0 0 20px', textAlign: 'center' }}>{heading}</h1>

          {/* Error summary */}
          {errors.form && (
            <div role="alert" aria-live="assertive" style={{ background: 'var(--color-background-danger)', border: '0.5px solid var(--color-border-danger)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--color-text-danger)', marginBottom: 16, display: 'flex', gap: 8 }}>
              <span aria-hidden="true">⚠</span><span>{errors.form}</span>
            </div>
          )}

          {done ? (
            <div role="status" style={{ background: 'var(--color-background-success)', border: '0.5px solid var(--color-border-success)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--color-text-success)' }}>
              {isInvite ? 'Your account is active.' : 'Your password has been updated.'} Taking you to sign in…
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate aria-label={isInvite ? 'Set password form' : 'Reset password form'}>
              <fieldset style={sw}>
                <legend style={{ ...sw, fontSize: 0 }}>{heading}</legend>
                <Field label="New password" name="password" error={errors.password} hint="At least 8 characters" required>
                  {ariaProps => <input {...ariaProps} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" disabled={!token} style={{ width: '100%', boxSizing: 'border-box' }} />}
                </Field>
                <Field label="Confirm password" name="confirm" error={errors.confirm} required>
                  {ariaProps => <input {...ariaProps} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" disabled={!token} style={{ width: '100%', boxSizing: 'border-box' }} />}
                </Field>
                <AccessibleButton type="submit" variant="primary" loading={loading} loadingLabel="Saving…" disabled={!token} style={{ width: '100%', marginTop: 8 }}>
                  {cta}
                </AccessibleButton>
              </fieldset>
            </form>
          )}

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Link to="/login" style={{ color: 'var(--color-text-secondary)', fontSize: 13, textDecoration: 'none' }}>
              ← Back to sign in
            </Link>
          </div>
        </section>
      </div>
    </main>
  )
}
